import { createContext, useCallback, useContext, useEffect, useState, ReactNode } from 'react';

const STORAGE_KEY = 'playground:admin-mode';

interface AdminModeContextValue {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  toggle: () => void;
}

const AdminModeContext = createContext<AdminModeContextValue | null>(null);

function readPersisted(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return localStorage.getItem(STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function AdminModeProvider({ children }: { children: ReactNode }) {
  const [enabled, setEnabledState] = useState<boolean>(readPersisted);

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, enabled ? 'true' : 'false');
    } catch {
      /* private browsing — silent */
    }
  }, [enabled]);

  const setEnabled = useCallback((next: boolean) => setEnabledState(next), []);
  const toggle = useCallback(() => setEnabledState(v => !v), []);

  return (
    <AdminModeContext.Provider value={{ enabled, setEnabled, toggle }}>
      {children}
    </AdminModeContext.Provider>
  );
}

export function useAdminMode(): AdminModeContextValue {
  const ctx = useContext(AdminModeContext);
  if (!ctx) throw new Error('useAdminMode must be used inside <AdminModeProvider>');
  return ctx;
}
